import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Loader2, UserPlus } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const ROLES = [
  { value: "hr_staff", label: "HR Staff" },
  { value: "agency_admin", label: "Agency Admin" },
  { value: "employee", label: "Employee" },
];
const PLATFORM_ROLES = [...ROLES, { value: "platform_owner", label: "Platform Owner" }];

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("hr_staff");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      await api.post("/auth/users", { name, email, role });
      setName(""); setEmail(""); setRole("hr_staff"); setShow(false);
      load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const availableRoles = user?.role === "platform_owner" ? PLATFORM_ROLES : ROLES;

  return (
    <div className="space-y-5">
      <div className="surface p-5 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="heading-section">Team Members</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Invite agency_admin and hr_staff to manage the platform.</p>
          </div>
          <button onClick={() => setShow((v) => !v)} className="btn-primary" data-testid="invite-user-button">
            <UserPlus className="w-4 h-4" /> Invite User
          </button>
        </div>

        {show && (
          <form onSubmit={invite} className="border border-border rounded-lg p-4 mb-4 bg-secondary/20 space-y-3" data-testid="invite-user-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium block mb-1">Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} required className="input-base" data-testid="invite-name" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="input-base" data-testid="invite-email" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Role</label>
                <select value={role} onChange={(e) => setRole(e.target.value)} className="input-base" data-testid="invite-role">
                  {availableRoles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShow(false)} className="btn-ghost">Cancel</button>
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="invite-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send invitation"}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading…</div>
        ) : users.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No users yet.</div>
        ) : (
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-secondary/40">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold">Name</th>
                  <th className="px-3 py-2 font-semibold">Email</th>
                  <th className="px-3 py-2 font-semibold">Role</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Last Login</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2.5 font-medium">{u.name}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{u.email}</td>
                    <td className="px-3 py-2.5 capitalize">{u.role.replace(/_/g, " ")}</td>
                    <td className="px-3 py-2.5">
                      {u.password_set ? <span className="pill-success">Active</span> : <span className="pill-warning">Pending Setup</span>}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground text-xs">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "Never"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
